import { useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { Autocomplete, Stack, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import BackButton from 'src/components/BackButton';
import HookFormCheckbox from 'src/components/hookform/HookFormCheckbox';
import HookFormTextField from 'src/components/hookform/HookFormTextField';
import StatusChip from 'src/components/StatusChip';
import SuccessButton from 'src/components/SuccessButton';
import ViewByToggle from 'src/components/ViewByToggle';
import { useTagOptions } from 'src/hooks/tag';
import { useCanViewAutomatedDeploymentSettings } from 'src/utils/featureFlags';

const DEPLOYMENT_TARGET = {
  SelectedTags: 'SelectedTags',
  AllDevices: 'AllDevices',
};

const DEPLOYMENT_TARGET_OPTIONS = [
  { value: DEPLOYMENT_TARGET.SelectedTags, label: 'Selected Tags' },
  { value: DEPLOYMENT_TARGET.AllDevices, label: 'All Devices' },
];

const SECTION_TITLE_SX = {
  color: '#9CA3AF',
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

export const ENVIRONMENT_DEFAULT_HELP =
  'Pre-selected when generating supplemental policies and catalogs for this environment, and what an installation import builds an app with no artifacts yet against.';

const schema = Yup.object().shape({
  policyDescription: Yup.string().trim(),
  environmentDefault: Yup.boolean().required(),
  enableBasePolicyDeployment: Yup.boolean().required(),
  enableSupplementalPolicyDeployment: Yup.boolean().required(),
  deploymentTarget: Yup.string().required(),
  // Not required: an empty list is a valid state in either direction - no tags to deploy to yet, or
  // nothing excluded from a deployment that covers every device. Yup treats [] as missing.
  tagsToDeployTo: Yup.array().default([]),
  tagsToExclude: Yup.array().default([]),
});

// The saved shape is one list of tags whose meaning flips on forAllDevices, but switching the
// target mid-edit should not throw away the other list's picks, so the form holds both.
const toDefaultValues = settings => ({
  policyDescription: settings.policyDescription || '',
  environmentDefault: !!settings.environmentDefault,
  enableBasePolicyDeployment: !!settings.enableBasePolicyDeployment,
  enableSupplementalPolicyDeployment: !!settings.enableSupplementalPolicyDeployment,
  deploymentTarget: settings.forAllDevices
    ? DEPLOYMENT_TARGET.AllDevices
    : DEPLOYMENT_TARGET.SelectedTags,
  tagsToDeployTo: settings.forAllDevices ? [] : settings.tags || [],
  tagsToExclude: settings.forAllDevices ? settings.tags || [] : [],
});

const toPayload = values => {
  const forAllDevices = values.deploymentTarget === DEPLOYMENT_TARGET.AllDevices;

  return {
    policyDescription: values.policyDescription?.trim() || null,
    environmentDefault: values.environmentDefault,
    enableBasePolicyDeployment: values.enableBasePolicyDeployment,
    enableSupplementalPolicyDeployment: values.enableSupplementalPolicyDeployment,
    forAllDevices,
    tagIds: (forAllDevices ? values.tagsToExclude : values.tagsToDeployTo).map(tag => tag.id),
  };
};

const EditManagedBasePolicyForm = ({ settings, loading, onCancel, onSubmit }) => {
  const theme = useTheme();
  const canViewDeploymentSettings = useCanViewAutomatedDeploymentSettings();
  const [tagSearch, setTagSearch] = useState('');

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: toDefaultValues(settings),
    resolver: yupResolver(schema),
  });

  const { control, handleSubmit, watch } = formMethods;

  const { data: tagData, isLoading: tagOptionsLoading } = useTagOptions({
    search: tagSearch,
    enabled: canViewDeploymentSettings,
  });

  const isAllDevices = watch('deploymentTarget') === DEPLOYMENT_TARGET.AllDevices;
  const tagFieldName = isAllDevices ? 'tagsToExclude' : 'tagsToDeployTo';
  const tagChipPalette = isAllDevices ? theme.palette.error : theme.palette.success;

  return (
    <form onSubmit={handleSubmit(values => onSubmit(toPayload(values)))}>
      <Stack spacing={2}>
        <HookFormTextField
          name="policyDescription"
          label="Policy description"
          formMethods={formMethods}
          placeholder="Optional"
          size="small"
          multiline
          minRows={2}
          maxRows={4}
          fullWidth
        />
        <Stack spacing={1.5}>
          <HookFormCheckbox
            name="environmentDefault"
            label="Environment default"
            helperText={ENVIRONMENT_DEFAULT_HELP}
            formMethods={formMethods}
          />
          {canViewDeploymentSettings ? (
            <>
              <HookFormCheckbox
                name="enableBasePolicyDeployment"
                label="Deploy base policy"
                helperText="Allow the agent to deploy this base policy to devices."
                formMethods={formMethods}
              />
              <HookFormCheckbox
                name="enableSupplementalPolicyDeployment"
                label="Deploy supplemental components"
                helperText="Allow the agent to deploy the supplemental policies and catalogs attached to it."
                formMethods={formMethods}
              />
            </>
          ) : null}
        </Stack>

        {canViewDeploymentSettings ? (
          <>
            <Stack gap={0.75}>
              <Typography sx={SECTION_TITLE_SX}>Deploy To</Typography>
              <Controller
                name="deploymentTarget"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ViewByToggle
                    label=""
                    value={value}
                    options={DEPLOYMENT_TARGET_OPTIONS}
                    onChange={onChange}
                  />
                )}
              />
            </Stack>

            <Stack gap={0.75}>
              <Typography sx={SECTION_TITLE_SX}>
                {isAllDevices
                  ? 'Select Tags to Exclude From Deployment'
                  : 'Select Tags To Deploy This Policy To'}
              </Typography>
              <Controller
                key={tagFieldName}
                name={tagFieldName}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    multiple
                    options={tagData?.tags || []}
                    loading={tagOptionsLoading}
                    value={value}
                    onChange={(event, newValue) => onChange(newValue)}
                    onInputChange={(event, newInput, reason) => {
                      if (reason === 'input') {
                        setTagSearch(newInput);
                      }
                    }}
                    getOptionLabel={option => option?.name || ''}
                    isOptionEqualToValue={(option, selected) => option?.id === selected?.id}
                    renderTags={(tags, getTagProps) =>
                      tags.map((option, index) => (
                        <StatusChip
                          label={option.name}
                          color={tagChipPalette.main}
                          backgroundColor={tagChipPalette.main}
                          textColor={tagChipPalette.contrastText}
                          sx={{ border: 'none' }}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder={isAllDevices ? 'Search tags to exclude' : 'Search tags'}
                      />
                    )}
                  />
                )}
              />
            </Stack>
          </>
        ) : null}

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <BackButton disabled={loading} onClick={onCancel}>
            Cancel
          </BackButton>
          <SuccessButton type="submit" disabled={loading} loading={loading}>
            Save
          </SuccessButton>
        </Stack>
      </Stack>
    </form>
  );
};

EditManagedBasePolicyForm.propTypes = {
  settings: PropTypes.shape({
    policyDescription: PropTypes.string,
    environmentDefault: PropTypes.bool,
    enableBasePolicyDeployment: PropTypes.bool,
    enableSupplementalPolicyDeployment: PropTypes.bool,
    forAllDevices: PropTypes.bool,
    tags: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  }).isRequired,
  loading: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default EditManagedBasePolicyForm;
